import * as d3 from 'd3';
import { type SelectedChartSegment } from '@/app/config/chart-segments';

export interface PieChartItem {
  header: string;
  value: number;
  color: string;
  segmentIndex: number;
}

interface DrawPieChartProperties {
  element: HTMLDivElement;
  chartItems: PieChartItem[];
  selectedSegment: SelectedChartSegment | null;
  indexKey: string;
  onSegmentClick?: (chartItem: PieChartItem) => void;
  innerRadius?: number;
  padAngle?: number;
  interactive?: boolean;
}

const boxSize = 1000;
const width = 760;
const height = 760;
const outerRadius = Math.min(width, height) / 2;
const defaultInnerRadius = 230;

type PieDatum = d3.PieArcDatum<PieChartItem>;
type PiePathElement = SVGPathElement & { previousArc?: PieDatum };

const drawPieChart = ({
  element,
  chartItems,
  selectedSegment,
  onSegmentClick,
  innerRadius = defaultInnerRadius,
  padAngle = 0.02,
  interactive = true,
}: DrawPieChartProperties): void => {
  const nonZeroItems = chartItems.filter((item) => item.value > 0);

  const pieGenerator = d3
    .pie<PieChartItem>()
    .padAngle(padAngle)
    .value((item) => item.value);

  const arcGenerator = d3.arc<PieDatum>().innerRadius(innerRadius).outerRadius(outerRadius);

  const existingSvg = element.querySelector('svg');

  const svg: d3.Selection<SVGSVGElement, unknown, null, undefined> = existingSvg
    ? d3.select<SVGSVGElement, unknown>(existingSvg)
    : d3
        .select(element)
        .append('svg')
        .attr('role', 'presentation')
        .attr('preserveAspectRatio', 'xMidYMid meet')
        .attr('height', '100%')
        .attr('width', '100%')
        .attr('viewBox', `0 0 ${boxSize} ${boxSize}`);

  let g = svg.select<SVGGElement>('g.pie-root');
  if (g.empty()) {
    g = svg
      .append('g')
      .attr('class', 'pie-root')
      .attr('transform', `translate(${boxSize / 2}, ${boxSize / 2})`);
  }

  const arcsData = pieGenerator(nonZeroItems);
  const transitionDuration = 200;

  const paths = g.selectAll<PiePathElement, PieDatum>('path.pie-segment').data(arcsData, (d) => d.data.header);

  // exit
  paths
    .exit()
    .transition()
    .duration(transitionDuration)
    .attrTween('d', function arcAttributeTween(this: PiePathElement, datum: unknown) {
      const d = datum as PieDatum;
      const current: PieDatum = this.previousArc ?? d;

      const end: PieDatum = {
        data: current.data,
        value: current.value,
        index: current.index,
        padAngle: current.padAngle,
        startAngle: current.endAngle,
        endAngle: current.endAngle,
      };

      const interpolate = d3.interpolate(current, end);

      return (t: number) => arcGenerator(interpolate(t)) ?? '';
    })
    .remove();

  // enter
  const entered = paths
    .enter()
    .append('path')
    .attr('class', 'pie-segment')
    .style('fill', (d) => d.data.color)
    .style('transition', 'opacity 150ms ease-out')
    .style('cursor', interactive ? 'pointer' : 'default')
    .each(function each(this: PiePathElement, datum: PieDatum) {
      const start: PieDatum = {
        data: datum.data,
        value: datum.value,
        index: datum.index,
        padAngle: datum.padAngle,
        startAngle: datum.startAngle,
        endAngle: datum.startAngle,
      };

      this.previousArc = start;
      d3.select(this).attr('d', arcGenerator(start) ?? '');
    });

  if (interactive && onSegmentClick) {
    entered.on('click', (_event, d) => {
      onSegmentClick(d.data);
    });
  }

  // update / merge
  const merged = entered.merge(paths);

  const hasSelection = selectedSegment !== null;
  const selectedIndex = hasSelection ? selectedSegment.segmentIndex : null;

  merged
    .style('opacity', (d) => {
      if (!hasSelection) return 1;
      return d.data.segmentIndex === selectedIndex ? 1 : 0.3;
    })
    .transition()
    .duration(transitionDuration)
    .ease(d3.easeCubicOut)
    .attrTween('d', function arcAttributeTween(this: PiePathElement, datum: unknown) {
      const d = datum as PieDatum;
      const previous: PieDatum = this.previousArc ?? d;
      const interpolate = d3.interpolate(previous, d);

      return (t: number) => {
        const interpolated = interpolate(t);
        this.previousArc = interpolated;
        return arcGenerator(interpolated) ?? '';
      };
    });
};

export default drawPieChart;
